import React, { useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { DragOverlay, useDndMonitor } from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { CSS, getEventCoordinates } from '@dnd-kit/utilities';
import log from 'chameleon/ui-stack/utilities/log';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import { toast } from 'chameleon/ui-stack/ui/toast';

const DropOnLabelEvent = 'labeldrag';

import { useTeacherOrganizeContext } from 'legacy-src/components/TeacherOrganize/hooks/useTeacherOrganize';

const alignToMouse = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (draggingNodeRect && activatorEvent) {
    const activatorCoordinates = getEventCoordinates(activatorEvent);

    if (!activatorCoordinates) {
      return transform;
    }

    const offsetX = activatorCoordinates.x - draggingNodeRect.left;
    const offsetY = activatorCoordinates.y - draggingNodeRect.top;

    return {
      ...transform,
      x: transform.x + offsetX - 15,
      y: transform.y + offsetY - 15,
    };
  }

  return transform;
};

const useLabelsDnD = ({ onLabelAdded, sendTelemetry }) => {
  const overElement = useRef();
  const { actions: labelActions } = useTeacherOrganizeContext({});

  const onDragMove = ({ over, active }) => {
    const type = active.id.split('/')[0];
    if (over && over.data.current.supports.includes(type)) {
      overElement.current = over;
    } else {
      overElement.current = null;
    }
  };

  const update = (assessment, label) =>
    labelActions
      .createLabelAssociation({
        resourceId: assessment.id,
        labelId: label.id,
        title: assessment.title,
        type: assessment.type,
      })
      .then((response) => {
        if (response.success) {
          assessment.title &&
            toast(`Your labels have been updated for ${assessment.title}`);
          onLabelAdded(assessment.id, Number(label.id));
          const telemetryParams = {
            associationId: response.data.id.toString(),
            labelId: label.id,
            label: label.name,
            assessmentId: assessment.id,
            assessmentType: assessment.type,
            assessment: assessment.title,
            timestamp: dayjs().utc().format(),
          };
          sendTelemetry(DropOnLabelEvent, telemetryParams);
          window.heap && window.heap.track(DropOnLabelEvent, telemetryParams);
        } else {
          log.error(
            `Could not create label(${label.name}) -> resource(${assessment.title}) association: `,
            response.error,
          );
        }
      });

  const onDragEnd = ({ over, active }) => {
    const type = active.id.split('/')[0];
    if (over && over.data.current.supports.includes(type)) {
      // Call the API
      update(active.data.current, over.data.current);
    }
  };

  useDndMonitor({
    onDragMove,
    onDragEnd,
  });

  const LabelOverlay = useMemo(() => {
    const dropAnimationConfig = {
      keyframes({ transform, active }) {
        let [x, y] = [0, 0];

        // Do a nice transition over the selected label
        // Which will always be the latest `over`
        if (overElement.current) {
          x -= active.rect.left;
          y -= active.rect.top - overElement.current.rect.top;
        }

        return [
          { transform: CSS.Transform.toString(transform.initial) },
          {
            transform: `translate(${x}px, ${y}px) scale(0.1, 0) skew(10deg, -2deg)`,
          },
        ];
      },
      sideEffects({ active }) {
        active.node.style.opacity = '1';
      },
    };

    return (
      <DragOverlay
        className="dragging-label cursor-grabbing"
        dropAnimation={dropAnimationConfig}
        style={{
          // Because dnd-kit sets overlay width equal to the dragged element
          // we have to enforce a predefined value here
          // since the overlay always has the same width
          width: 380,
          height: 54,
        }}
        modifiers={[alignToMouse, restrictToWindowEdges]}
      >
        <div className="px-4 py-8 bg-gray-900 rounded text-white">
          <div className="flex align-center gap-x-4 justify-start">
            <Icon id="label_important" />
            <span>Drag onto label name on the left to add label</span>
          </div>
        </div>
      </DragOverlay>
    );
  });

  return {
    LabelOverlay,
  };
};

export default useLabelsDnD;
